import { EditOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { Button, Modal, Tooltip } from "antd";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { handleRequestStoryMedia, handleUpdateStoryAiConfig } from "store/StoryAiCofig/thunks";
import SunEditor from "suneditor-react";

const BLOCK_TAGS = new Set(["P", "DIV", "LI", "H1", "H2", "H3", "H4", "H5", "H6"]);

const escapeHtml = (value = ""): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeAllowedBoldTags = (value = ""): string =>
  value.replace(/<\s*strong\s*>/gi, "<b>").replace(/<\s*\/\s*strong\s*>/gi, "</b>");

const toEditorHtml = (value = ""): string => {
  const normalized = normalizeAllowedBoldTags(value);
  const placeholders: string[] = [];
  const masked = normalized.replace(/<\/?b>/gi, (tag) => {
    const key = `__BOLD_${placeholders.length}__`;
    placeholders.push(tag.toLowerCase());
    return key;
  });
  const escaped = escapeHtml(masked);
  const restored = placeholders.reduce((result, tag, index) => {
    return result.replaceAll(`__BOLD_${index}__`, tag);
  }, escaped);
  return restored
    .split("\n")
    .map((line) => (line.length ? `<p>${line}</p>` : "<p><br></p>"))
    .join("");
};

const toRequiredText = (html = ""): string => {
  if (!html) return "";
  const root = document.createElement("div");
  root.innerHTML = normalizeAllowedBoldTags(html);

  const walk = (node: any): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    const element = node as HTMLElement;
    const tag = element.tagName;
    if (tag === "BR") return "\n";
    const childrenText = Array.from(element.childNodes).map(walk).join("");
    if (tag === "B") return `<b>${childrenText}</b>`;
    if (BLOCK_TAGS.has(tag)) return `${childrenText}\n`;
    return childrenText;
  };

  return Array.from(root.childNodes)
    .map(walk)
    .join("")
    .replace(/<b>\s*<\/b>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

type StoryTextEditorProps = {
  storyInfo?: Record<string, any>;
  text?: string;
  onSave?: (value: string) => void;
};

const StoryTextEditor: React.FC<StoryTextEditorProps> = ({
  storyInfo = {},
  text = "",
  onSave = () => {},
}) => {
  const showAlert = useAlert();
  const dispatch = useDispatch<any>();
  const editorRef = useRef<any>(null);
  const [visible, setVisible] = useState<boolean>(false);
  const [editorHtml, setEditorHtml] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    setEditorHtml(toEditorHtml(text || ""));
  }, [text, visible]);

  const handleSave = async () => {
    const value = toRequiredText(editorRef.current?.editor?.getContents() || editorHtml);
    const textWithoutBoldTags = value.replace(/<\/?b>/gi, "");
    if (!textWithoutBoldTags.trim()) {
      showAlert("Please enter story content before saving.", "warning");
      return;
    }

    try {
      setIsSaving(true);
      const content =
        typeof storyInfo?.content === "object" && storyInfo?.content ? storyInfo.content : {};
      const payload = {
        id: storyInfo?.id,
        content: {
          ...content,
          story_content: value,
        },
        story_content: value,
      };
      const response = await dispatch(handleUpdateStoryAiConfig(payload,'story_content'));
      if (response && (response.success || response.id || response?.data?.id)) {
        dispatch(handleRequestStoryMedia({ story_id: storyInfo?.id, generate_image: false }));
        onSave(value);
        setVisible(false);
        showAlert("Story updated successfully.", "success");
      } else {
        showAlert("Error in updating story.", "error");
      }
    } catch (error) {
      Sentry.captureException(error);
      showAlert("Error in updating story.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Tooltip title="Edit Story">
        <Button
          icon={<EditOutlined style={{ color: "#fff" }} />}
          onClick={() => setVisible(true)}
          loading={isSaving}
          style={{
            borderRadius: 10,
            border: "0px",
            color: "#fff",
            backgroundColor: "#03989e",
            minWidth: 50,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      </Tooltip>
      <Modal
        title="Edit Story"
        open={visible}
        onOk={handleSave}
        onCancel={() => setVisible(false)}
        okText="Save"
        confirmLoading={isSaving}
        width={900}
        bodyStyle={{ padding: 0 }}
        destroyOnClose
      >
        <SunEditor
          getSunEditorInstance={(editor: any) => {
            editorRef.current = { editor };
          }}
          setContents={editorHtml}
          onLoad={() => {
            editorRef.current?.editor?.core?.history?.reset(true);
          }}
          setOptions={{
            height: "420",
            showPathLabel: false,
            buttonList: [["undo", "redo", "bold"]],
          }}
        />
      </Modal>
    </>
  );
};

export default StoryTextEditor;
